import get from "lodash.get"
import { getEntityClientRoute } from "./entityUtils.mjs"

/**
 * Finds the index in the header row of a table that corresponds to a given value.
 *
 * If the `value` is a non-numeric string, it returns the index of the header matching that string.
 * If the `value` is numeric or falsy, it finds the index of the first header where `value` is
 * less than or equal to the header value and greater than the previous header value.
 * If no such header is found, it returns the last header index.
 * If the found index is `0`, it defaults to `1`.
 *
 * @param {Array<Array<any>>} [table=[]] - A 2D array representing the table, with the first row as headers.
 * @param {string|number} value - The value to find the corresponding header index for.
 * @returns {number} The index in the header row matching the value.
 *
 * @example
 * findYIndex([["A", "B", "C"]], "B") // 1
 * findYIndex([[10, 20, 30]], 15) // 1 (15 is between 10 and 20)
 * findYIndex([[10, 20, 30]], 35) // 2 (greater than max header)
 * findYIndex([[10, 20, 30]], 0) // 1 (default fallback)
 */
function findYIndex(table = [], value) {
  const headers = table[0] || []

  let index
  if (value && isNaN(value)) {
    return headers.findIndex(header => header === value)
  } else {
    const number = value || 0
    index = headers.findIndex((value, index) => number <= value && number > headers[index - 1])
    if (index < 0) index = headers.length - 1 // If number > max header then take the last one
    if (!index) index = 1
  }
  return index
}

/**
 * Finds the index of a row in a table based on one or two values, supporting 2D or 3D-like data.
 *
 * - For tables with three-dimensional data (rows where first two elements are values):
 *   - If both `value` and `value2` are strings, finds the row matching both exactly.
 *   - If one is a string and the other a number, matches the string exactly and compares the number with >=.
 *   - If both are numbers, finds the first row where both are >= values.
 * - For simpler tables (2D), matches based on `value` either by exact string match or >= number.
 * - If no matching row is found, returns the last index in the table.
 *
 * @param {Array<Array<any>>} table - The input table array.
 * @param {string|number} value - The first value to match or compare.
 * @param {string|number} value2 - The second value to match or compare (optional for 3D).
 * @returns {number} The index of the matching row or the last index if no match found.
 *
 * @example
 * // 3D-like table exact match
 * findXIndex([["A", "X", 1], ["B", "Y", 2]], "A", "X") // 0
 *
 * // 3D-like table partial match with number
 * findXIndex([["A", 10], ["B", 20]], "A", 5) // 0
 *
 * // 2D table string match
 * findXIndex([["A", 1], ["B", 2]], "B") // 1
 *
 * // 2D table numeric comparison
 * findXIndex([[5], [10], [15]], 7) // 1 (first row where value >= 7)
 */
function findXIndex(table, value, value2) {
  const hasThreeDimensions = table[0] && !table[0][0] && !table[0][1]
  let index
  if (hasThreeDimensions && value && value2) {
    if (typeof value === "string" && typeof value2 === "string") index = table.findIndex(row => row && row[0] === value && row[1] === value2)
    else if (typeof value === "string") index = table.findIndex(row => row && row[0] === value && row[1] >= value2)
    else if (typeof value2 === "string") index = table.findIndex(row => row && row[0] >= value && row[1] === value2)
    else {
      index = table.findIndex(row => row && row[0] >= value && row[1] >= value2)
    }
  } else {
    if (typeof value === "string") index = table.findIndex(row => row && row[0] === value)
    else index = table.findIndex(row => row && row[0] >= value)
  }
  if (index === -1) return table.length - 1
  return index
}

/**
 * Retrieves a value from a 2D or 3D-like table based on given x and y coordinates.
 *
 * Uses `findXIndex` to locate the row index based on `xValue` (and optionally `x2Value`),
 * and `findYIndex` to locate the column index based on `yValue`.
 * Returns the value at the intersection of the found row and column.
 * If the table is falsy, returns `undefined`.
 *
 * @param {Array<Array<any>>} table - The table to search within.
 * @param {string|number} xValue - The primary x-axis value for row lookup.
 * @param {string|number} yValue - The y-axis value for column lookup.
 * @param {string|number} [x2Value] - Optional secondary x-axis value for 3D-like tables.
 * @returns {any} The value found at the located row and column, or `undefined` if not found.
 *
 * @example
 * findTableValue(
 *   [["A", "B", "C"], [1, 10, 20], [2, 30, 40]],
 *   1, "B"
 * ) // 10
 *
 * @example
 * findTableValue(
 *   [["A", "X", 100], ["B", "Y", 200]],
 *   "B", "X", "Y"
 * ) // 200 (depends on `findXIndex` and `findYIndex`)
 */
function findTableValue(table, xValue, yValue, x2Value) {
  if (!table) return
  const x = findXIndex(table, xValue, x2Value)
  const y = findYIndex(table, yValue)
  return table?.[x]?.[y]
}

/**
 * Recursively replaces variables in an object or array using provided data.
 *
 * - Traverses nested arrays and objects, applying replacements to string values.
 * - Leaves functions unchanged.
 * - If `options.useParsePath` is true and the value is a string enclosed in `{}`,
 *   it parses the path inside the braces using `parsePath` before replacing variables.
 * - Uses `replaceVariables` to perform the variable replacements.
 *
 * @param {*} obj - The object, array, string, or other value to process.
 * @param {Object} [data={}] - The data source for variable replacement.
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.useParsePath] - Whether to parse paths enclosed in `{}` before replacement.
 * @returns {*} The new object/array/value with variables replaced.
 *
 * @example
 * deepReplaceVariables(
 *   { greeting: "Hello {name}" },
 *   { name: "Alice" }
 * ) // { greeting: "Hello Alice" }
 *
 * @example
 * deepReplaceVariables(
 *   ["Value is {value}", () => "function"],
 *   { value: 42 },
 *   { useParsePath: true }
 * ) // ["Value is 42", () => "function"]
 */
function deepReplaceVariables(obj, data = {}, options) {
  if (Array.isArray(obj)) {
    return obj.map(item => deepReplaceVariables(item, data))
  } else if (obj !== null && typeof obj === "object") {
    const result = {}
    for (let key in obj) {
      if (obj[key]) {
        result[key] = deepReplaceVariables(obj[key], data)
      }
    }
    return result
  } else if (["function", "boolean"].includes(typeof obj)) {
    return obj // Leave function/boolean as is
  } else {
    if (options?.useParsePath && typeof obj === "string") {
      const isEnclosed = obj.startsWith("{") && obj.endsWith("}")
      const path = isEnclosed ? obj.substring(1, obj.length - 1) : obj
      const parsedPath = parsePath(data, path)
      return replaceVariables(isEnclosed ? `{${parsedPath}}` : parsedPath, data)
    } else {
      return replaceVariables(obj, data)
    }
  }
}

/**
 * Replaces placeholders in a string with corresponding values from a data object.
 *
 * - Replaces "{entityName}" with the client route from `getEntityClientRoute` when `options.getEntityNameClientRoute` is truthy and the string contains that placeholder.
 * - Replaces placeholders in curly braces `{key}` with matching values from `data`.
 * - Replaces colon-prefixed parameters like `:key` followed by `/`, `?`, or `&` with corresponding data values.
 * - If a placeholder value is missing in `data`, it leaves the placeholder unchanged.
 *
 * @param {string} [str=""] - The string containing placeholders to replace.
 * @param {Object} [data={}] - The data object providing replacement values.
 * @param {Object} [options] - Optional settings.
 * @param {*} [options.getEntityNameClientRoute] - If truthy, `{entityName}` is replaced using `getEntityClientRoute` with `data.entityName`.
 * @returns {string} The string with placeholders replaced by data values.
 *
 * @example
 * replaceVariables("/contract/{registration}", { registration: "FIN123" })
 * // "/contract/FIN123"
 *
 * @example
 * replaceVariables("/contract/:registration", { registration: "FIN123" })
 * // "/contract/FIN123"
 *
 * @example
 * replaceVariables("Entity: {entityName}", { entityName: "User" }, { getEntityNameClientRoute: name => `/client/${name}` })
 * // "Entity: /client/User"
 */
function replaceVariables(str = "", data = {}, options) {
  const { getEntityNameClientRoute } = options || {}

  const entityNameHint = "{entityName}"
  if (getEntityNameClientRoute && str.includes(entityNameHint)) {
    str = str.replace(entityNameHint, getEntityClientRoute(get(data, "entityName")))
  }

  // /contract/{registration} => /contract/FINXXX
  if (str.includes("{")) str = str.replace(/{([^{}]*)}/g, (match, key) => get(data, key) ?? match)

  // /contract/:registration => /contract/FINXXX
  if (str.includes(":")) {
    const regex = new RegExp(":(" + Object.keys(data).join("|") + ")[/|?|&]", "g")
    str = (str + "/").replace(regex, (m, $1) => (data[$1] ? data[$1] + m[m.length - 1] : m))
    str = str.substring(0, str.length - 1)
  }

  return str
}

/**
 * Finds a value in a 2D table based on row and column parameters by locating the interval they fit into.
 *
 * - Searches the first column to find the row where `rowParam` lies between two consecutive values.
 * - Searches the first row to find the column where `colParam` lies between two consecutive values.
 * - Returns the value at the intersection of the found row and column.
 * - If no matching interval is found for either parameter, returns `undefined`.
 *
 * @param {Array<Array<number>>} array - The 2D table with the first row and first column as headers.
 * @param {number} rowParam - The value to locate along the first column (row headers).
 * @param {number} colParam - The value to locate along the first row (column headers).
 * @returns {number|undefined} The table value at the found intersection or `undefined` if not found.
 *
 * @example
 * const table = [
 *   [null, 10, 20, 30],
 *   [5,    1,  2,  3],
 *   [15,   4,  5,  6],
 *   [25,   7,  8,  9]
 * ];
 * findTableEntry(table, 12, 15) // 5 (row between 5 and 15, col between 10 and 20)
 */
function findTableEntry(array, rowParam, colParam) {
  let i
  let j
  for (i = 1; i < array.length - 1; i++) {
    if (array[i][0] <= rowParam && array[i + 1][0] > rowParam) break
  }
  const row0 = array[0]
  for (j = 1; j < row0.length - 1; j++) {
    if (row0[j] <= colParam && row0[j + 1] > colParam) break
  }
  if (i && j) return array[i][j]
}

/**
 * Parses a complex object path string containing filters (e.g., `key=value`) into a valid path with array indices.
 *
 * - Splits the path by dots and brackets, supporting nested properties and arrays.
 * - When encountering a filter segment like `key=value`, finds the index of the first array element
 *   at the current path whose `key` matches `value` and replaces the filter with `[index]`.
 * - Returns the parsed path string with numeric indices in place of filters.
 * - If the path does not contain `=`, returns it unchanged.
 *
 * @param {Object} obj - The object to use for resolving filtered paths.
 * @param {string} path - The path string, which may include filters like `role=CLIENT`.
 * @returns {string} The parsed path string with filters replaced by array indices.
 *
 * @example
 * const data = {
 *   persons: [
 *     { role: "CLIENT", person: { registrations: [{ type: "SIR", number: "123" }] } },
 *     { role: "ADMIN", person: {} }
 *   ]
 * };
 * parsePath(data, "persons.role=CLIENT.person.registrations.type=SIR.number")
 * // "persons[0].person.registrations[0].number"
 * parsePath(data, "persons[role=CLIENT].person.registrations[type=SIR].number")
 * // "persons[0].person.registrations[0].number"
 */
function parsePath(obj, path) {
  if (!path.includes("=")) return path

  let parsedPath = ""

  // ["persons", "role=CLIENT", "person", "registrations", "type=SIR", "number"]
  const parts = path
    .split(".")
    .flatMap(part => (part.includes("[") ? part.split("[").flatMap(subPath => subPath.split("]")) : part))
    .filter(part => part)

  for (let [index, part] of parts.entries()) {
    // role=CLIENT
    if (part.includes("=") && index > 0) {
      const [key, value] = part.split("=") // ["role", "CLIENT"]
      const subEntities = get(obj, parsedPath) // entity.persons
      if (Array.isArray(subEntities)) {
        const subEntityIndex = subEntities.findIndex(e => e[key] === value)
        parsedPath += `[${subEntityIndex}]`
      }
    } else {
      parsedPath += index === 0 ? part : `.${part}`
    }
  }

  return parsedPath
}

export { deepReplaceVariables, findTableEntry, findTableValue, parsePath, replaceVariables }
